import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Upload, X } from "lucide-react";
import { Reveal, Eyebrow } from "./primitives";
import { API } from "../lib/site";

const REASONS = [
    "Contribution",
    "Volunteering",
    "Research collaboration",
    "Professional expertise",
    "Advocacy & awareness",
    "Other",
];

const CONTRIBUTIONS = [
    "Time & skills",
    "Research & knowledge",
    "Funding or sponsorship",
    "Professional expertise",
    "Advocacy & awareness",
    "Other",
];

const initialForm = {
    name: "",
    email: "",
    phone: "",
    country: "",
    profession: "",
    reason: "",
    message: "",
};

export const RegisterForm = () => {
    const [form, setForm] = useState(initialForm);
    const [selected, setSelected] = useState([]);
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const fileInput = useRef(null);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const toggleContribution = (c) => {
        setSelected((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));
        setErrors((er) => ({ ...er, contributions: undefined }));
    };

    const validate = () => {
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your full name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
        if (form.country.trim().length < 2) er.country = "Please enter your country.";
        if (!form.reason) er.reason = "Please choose your reason for participation.";
        if (selected.length === 0) er.contributions = "Please choose at least one way of contributing.";
        return er;
    };

    const submit = async (e) => {
        e.preventDefault();
        const er = validate();
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("submitting");
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            selected.forEach((c) => fd.append("contributions", c));
            if (file) fd.append("document", file);
            await axios.post(`${API}/register`, fd);
            setStatus("success");
            toast.success("Thank you — your registration has been received.");
        } catch (err) {
            setStatus("idle");
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
        }
    };

    const labelCls = "mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#1A3B2B]/70";
    const errCls = "mt-2 text-xs font-semibold text-red-800";

    if (status === "success") {
        return (
            <div
                data-testid="register-success"
                role="status"
                className="mt-10 rounded-2xl border border-[#1A3B2B]/20 bg-white p-10 shadow-[0_20px_60px_rgba(10,23,17,0.07)]"
            >
                <CheckCircle2 size={36} className="text-[#1A3B2B]" aria-hidden="true" />
                <p className="mt-5 text-base leading-relaxed text-[#0A1711] md:text-lg">
                    Thank you for contacting Global Green Hotel. We have received your message and
                    will respond as soon as possible.
                </p>
                <button
                    type="button"
                    data-testid="register-another"
                    onClick={() => {
                        setForm(initialForm);
                        setSelected([]);
                        setFile(null);
                        setStatus("idle");
                    }}
                    className="btn-outline-green mt-8"
                >
                    Register Someone Else
                </button>
            </div>
        );
    }

    return (
        <Reveal delay={0.15}>
            <form onSubmit={submit} noValidate data-testid="register-form" className="mt-10 grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                        <label htmlFor="rg-name" className={labelCls}>Full Name *</label>
                        <input
                            id="rg-name"
                            data-testid="register-name"
                            type="text"
                            value={form.name}
                            onChange={set("name")}
                            aria-invalid={!!errors.name}
                            className={`input-field ${errors.name ? "input-error" : ""}`}
                            placeholder="Your full name"
                        />
                        {errors.name && <p className={errCls} role="alert">{errors.name}</p>}
                    </div>
                    <div>
                        <label htmlFor="rg-email" className={labelCls}>Email *</label>
                        <input
                            id="rg-email"
                            data-testid="register-email"
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            aria-invalid={!!errors.email}
                            className={`input-field ${errors.email ? "input-error" : ""}`}
                            placeholder="you@example.com"
                        />
                        {errors.email && <p className={errCls} role="alert">{errors.email}</p>}
                    </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                        <label htmlFor="rg-phone" className={labelCls}>
                            Phone <span className="font-semibold normal-case tracking-normal text-[#4A5D54]/60">(optional)</span>
                        </label>
                        <input
                            id="rg-phone"
                            data-testid="register-phone"
                            type="tel"
                            value={form.phone}
                            onChange={set("phone")}
                            className="input-field"
                            placeholder="+1 …"
                        />
                    </div>
                    <div>
                        <label htmlFor="rg-country" className={labelCls}>Country *</label>
                        <input
                            id="rg-country"
                            data-testid="register-country"
                            type="text"
                            value={form.country}
                            onChange={set("country")}
                            aria-invalid={!!errors.country}
                            className={`input-field ${errors.country ? "input-error" : ""}`}
                            placeholder="Country"
                        />
                        {errors.country && <p className={errCls} role="alert">{errors.country}</p>}
                    </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                        <label htmlFor="rg-profession" className={labelCls}>
                            Profession / Background <span className="font-semibold normal-case tracking-normal text-[#4A5D54]/60">(optional)</span>
                        </label>
                        <input
                            id="rg-profession"
                            data-testid="register-profession"
                            type="text"
                            value={form.profession}
                            onChange={set("profession")}
                            className="input-field"
                            placeholder="e.g. chef, student, engineer…"
                        />
                    </div>
                    <div>
                        <label htmlFor="rg-reason" className={labelCls}>Reason for Participation *</label>
                        <select
                            id="rg-reason"
                            data-testid="register-reason"
                            value={form.reason}
                            onChange={set("reason")}
                            aria-invalid={!!errors.reason}
                            className={`input-field ${errors.reason ? "input-error" : ""}`}
                        >
                            <option value="">Select a reason…</option>
                            {REASONS.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                        {errors.reason && <p className={errCls} role="alert">{errors.reason}</p>}
                    </div>
                </div>

                <fieldset>
                    <legend className={labelCls}>Ways You Would Like to Contribute *</legend>
                    <div className="grid gap-3 sm:grid-cols-2" data-testid="register-contributions">
                        {CONTRIBUTIONS.map((c) => (
                            <label
                                key={c}
                                className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                                    selected.includes(c)
                                        ? "border-[#1A3B2B] bg-[#1A3B2B]/5 text-[#0A1711]"
                                        : "border-[#E8E6E1] bg-white text-[#4A5D54] hover:border-[#C2A160]"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    data-testid={`register-contribution-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    checked={selected.includes(c)}
                                    onChange={() => toggleContribution(c)}
                                    className="h-4 w-4 accent-[#1A3B2B]"
                                />
                                {c}
                            </label>
                        ))}
                    </div>
                    {errors.contributions && <p className={errCls} role="alert">{errors.contributions}</p>}
                </fieldset>

                <div>
                    <label htmlFor="rg-message" className={labelCls}>
                        Message <span className="font-semibold normal-case tracking-normal text-[#4A5D54]/60">(optional)</span>
                    </label>
                    <textarea
                        id="rg-message"
                        data-testid="register-message"
                        rows={4}
                        value={form.message}
                        onChange={set("message")}
                        className="input-field resize-y"
                        placeholder="Tell us how you would like to be part of the movement…"
                    />
                </div>

                <div>
                    <label className={labelCls} htmlFor="rg-document">
                        Supporting Document <span className="font-semibold normal-case tracking-normal text-[#4A5D54]/60">(optional — PDF, DOC, image or text, max 10 MB)</span>
                    </label>
                    {file ? (
                        <div className="flex items-center justify-between rounded-xl border border-[#1A3B2B]/25 bg-[#1A3B2B]/5 px-4 py-3">
                            <span className="truncate text-sm font-semibold text-[#0A1711]" data-testid="register-file-name">
                                {file.name}
                            </span>
                            <button
                                type="button"
                                data-testid="register-file-remove"
                                aria-label="Remove document"
                                onClick={() => {
                                    setFile(null);
                                    if (fileInput.current) fileInput.current.value = "";
                                }}
                                className="ml-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#1A3B2B]/20 text-[#1A3B2B] transition-colors hover:border-red-700 hover:text-red-700"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ) : (
                        <label
                            htmlFor="rg-document"
                            data-testid="register-file-drop"
                            className="flex cursor-pointer items-center justify-center gap-3 rounded-xl border border-dashed border-[#1A3B2B]/30 bg-white px-4 py-6 text-sm font-semibold text-[#4A5D54] transition-colors hover:border-[#C2A160] hover:text-[#1A3B2B]"
                        >
                            <Upload size={17} aria-hidden="true" />
                            Choose a document to upload
                        </label>
                    )}
                    <input
                        id="rg-document"
                        ref={fileInput}
                        data-testid="register-file-input"
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt"
                        className="sr-only"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            if (f.size > 10 * 1024 * 1024) {
                                toast.error("File is too large (maximum 10 MB).");
                                e.target.value = "";
                                return;
                            }
                            setFile(f);
                        }}
                    />
                </div>

                <div>
                    <button
                        type="submit"
                        data-testid="register-submit"
                        disabled={status === "submitting"}
                        className="btn-green disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {status === "submitting" ? "Submitting…" : "Register to Join the Movement"}
                    </button>
                </div>
            </form>
        </Reveal>
    );
};
